import { observer } from "mobx-react";
import { FC, ReactElement, useEffect, useMemo } from "react";
import {
  ShareholderAdditionalRepresentativesView,
  ShareholderCommitmentsView,
  ShareholderContributionsView,
  ShareholderDetailsLayout,
  ShareholderDetailsLoadingView,
  ShareholderDetailsOverview,
  ShareholderDetailsView,
  ShareholderTransactionsView,
} from "./shareholderDetailsComponents";
import {
  useDispatch,
  useI18n,
  useLanguage,
  useMessageStore,
  useRootStore,
  useShareholdersStore,
  useTranslation,
  useUserStore,
} from "@/stores/RootStoreContext";
import {
  isOptionHolderWithoutShares,
  prepAdditionalRepresentativesTable,
  prepCommitmentsData,
  prepContributionsView,
  prepDetailsCards,
  prepShareholderDetailsActions,
  prepShareholderDetailsDialogs,
  prepShareholderDetailsHeader,
  prepShareholdingAlert,
  prepShareholdingEmptyState,
  prepShareholdingSections,
  prepTransactionsSummaryInfo,
  prepTransactionsTable,
  prepTransactionSummaryCards,
  ShareDetailsDialogsProps,
  ShareholderDetailsActions,
  ShareholderDetailsContext,
  toShareholderDetailsTab,
} from "./shareholderDetailsDomain";
import { PerDateFilterAlert } from "./PerDateFilterAlert";
import { ShareholderOptionsTab } from "@/instruments/optionsComponents";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator";
import {
  clearOptionsState,
  initOptionsPage,
  isLoadingOptions,
  loadOptionsCapTable,
  optionHolderSince,
  optionsPageData,
} from "@/instruments/optionsDomain";
import { holderForShareholder, holderSelectorItems } from "@/instruments/instrumentHolders";
import { Dialog } from "@/components-v2/Dialog";
import {
  adminShareholderDetails,
  adminShareholders,
  adminShareholdersOptions,
  adminShareholdersShareholderCommitmentDetails,
  adminShareholdersShareholderCommitments,
  adminShareholdersShareholderContributionDetails,
  adminShareholdersShareholderContributions,
  adminShareholdersTransaction,
  navigationReferrerUrl,
} from "@/utils/navigation";
import { fromQueryString, singleValue, toQueryString } from "@/utils/queryParams";
import { isEmpty } from "lodash";
import AlertMessageStore from "@/stores/AlertMessageStore";
import { ShareholderDetailsState } from "@/stores/ShareholdersStore";
import { ShareholderCommitmentController } from "@/shareholderRegister/commitments/shareholderCommitmentsDomain";
import { ShareholderCommitmentsDialogs } from "@/shareholderRegister/commitments/ShareholderCommitmentsPage";
import {
  ShareholderPledgeForm,
  ShareholderRemarksForm,
} from "@/shareholderRegister/shareholderRegisterDomain";
import { Container, VBox } from "@/components-v2/Box";
import { YesNoQuestion } from "@/dbot/common/commonComponents";
import { TextArea } from "@/components-v2/TextArea";
import { Alert } from "@/components-v2/Alert";
import {
  isContributionsEnabled,
  ShareholderContributionController,
} from "@/shareholderRegister/contributions/shareholderContributionsDomain";
import { ShareholderContributionDialogs } from "@/shareholderRegister/contributions/ShareholderContributionsPage";
import { atTime } from "@/utils/Dates";

export const ShareholderDetailsTabView: FC<{
  context: ShareholderDetailsContext;
  state: ShareholderDetailsState;
  actions: ShareholderDetailsActions;
}> = observer(({ context, state, actions }) => {
  const language = useLanguage();
  switch (true) {
    case state.isLoading:
      return <ShareholderDetailsLoadingView />;
    case isOptionHolderWithoutShares(context, state) && context.activeTab !== "details":
      return (
        <ShareholderOptionsTab
          shareholderId={context.shareholderId}
          fairValues={state.shareholder?.fairValueInformation ?? []}
        />
      );
    case !context.activeTab || context.activeTab === "overview":
      return (
        <ShareholderDetailsOverview
          sections={prepShareholdingSections(state, actions, language)}
          emptyState={prepShareholdingEmptyState(context, state, actions)}
          alert={prepShareholdingAlert(context, state)}
        />
      );
    case context.activeTab === "transactions":
      return (
        <ShareholderTransactionsView
          transactionsSummaryInfo={prepTransactionsSummaryInfo(state)}
          transactionsSummaryCards={prepTransactionSummaryCards(state)}
          transactionsTable={prepTransactionsTable(state, actions)}
        />
      );
    case context.activeTab === "details":
      return <ShareholderDetailsView detailsCards={prepDetailsCards(context, state, actions)} />;
    case context.activeTab === "additional-representatives":
      return (
        <ShareholderAdditionalRepresentativesView
          additionalRepresentativesTable={prepAdditionalRepresentativesTable(state, actions)}
          inviteAdditionalRepresentative={actions.openInviteRepresentativeDialog}
          isEmpty={isEmpty(state.shareholder?.additionalRepresentatives)}
        />
      );
    case context.activeTab === "options" && context.optionsTabEnabled:
      return (
        <ShareholderOptionsTab
          shareholderId={context.shareholderId}
          fairValues={state.shareholder?.fairValueInformation ?? []}
        />
      );
    case context.activeTab === "commitments":
      return <ShareholderCommitmentsView {...prepCommitmentsData(context, state, actions)} />;
    case context.activeTab === "contributions":
      return <ShareholderContributionsView {...prepContributionsView(context, state, actions)} />;
    default:
      return null;
  }
});

export const ShareholderDetailsPageView: FC<{
  alertStore: AlertMessageStore;
  context: ShareholderDetailsContext;
  state: ShareholderDetailsState;
  actions: ShareholderDetailsActions;
  header?: ReactElement;
}> = observer(({ alertStore, context, state, actions, header }) => (
  <ShareholderDetailsLayout
    alertStore={alertStore}
    header={prepShareholderDetailsHeader(context, state, actions)}
    content={
      <>
        <div>{header}</div>
        <ShareholderDetailsTabView context={context} state={state} actions={actions} />
      </>
    }
  />
));

const ShareholderPledgeFormView = observer(({ form }: { form: ShareholderPledgeForm }) => {
  const { t } = useTranslation();
  return (
    <VBox>
      <YesNoQuestion question={t("shareholders.are-shares-pledged")} field={form.isPledged} />
      <TextArea
        label={t("shareholders.pledged-shares-comment")}
        value={form.comment.inputValue}
        onChange={form.comment.setInputValue}
        onBlur={form.comment.commit}
      />
      <Alert message={t("shareholders.pledged-shares.info")} type={"info"} />
    </VBox>
  );
});

const ShareholderRemarksFormView = observer(({ form }: { form: ShareholderRemarksForm }) => {
  const { t } = useTranslation();
  return (
    <VBox gap={"lg"}>
      <TextArea
        label={t("shareholders.edit-remarks.remark")}
        value={form.remark.inputValue}
        onChange={form.remark.setInputValue}
        onBlur={form.remark.commit}
      />
      <Alert
        message={t("shareholders.edit-remarks.info", { name: form.shareholderName })}
        type={"info"}
      />
    </VBox>
  );
});

export const ShareholderDetailsDialogs: FC<ShareDetailsDialogsProps> = observer((props) => {
  return (
    <>
      {(props.editPledge.form.mode === "edit" || props.editPledge.form.mode === "saving") && (
        <Dialog
          title={{ key: "shareholders.pledged-shares" }}
          onCancel={props.editPledge.onCancel}
          onSubmit={props.editPledge.onSubmit}
          isSubmitting={props.editPledge.form.mode === "saving"}
        >
          <ShareholderPledgeFormView form={props.editPledge.form} />
        </Dialog>
      )}
      {(props.editRemarks.form.mode === "edit" || props.editRemarks.form.mode === "saving") && (
        <Dialog
          title={{
            key: "shareholders.edit-remarks",
            params: { name: props.editRemarks.form.shareholderName },
          }}
          onCancel={props.editRemarks.onCancel}
          onSubmit={props.editRemarks.onSubmit}
          isSubmitDisabled={!props.editRemarks.form.isValid}
          isSubmitting={props.editRemarks.form.mode === "saving"}
        >
          <ShareholderRemarksFormView form={props.editRemarks.form} />
        </Dialog>
      )}
      <ShareholderCommitmentsDialogs controller={props.commitmentCtrl} />
      <ShareholderContributionDialogs controller={props.contributionCtrl} />
    </>
  );
});

export const ShareholderDetailsPage: FC<{
  id: string;
  tab?: string;
  allowCommitments: boolean;
}> = observer((props) => {
  const location = useRootStore().location;
  const queryString = location.search;
  const perDate = singleValue(fromQueryString(queryString).perDate);
  const validPerDate = perDate && /^\d{4}-\d{2}-\d{2}$/.test(perDate) ? perDate : undefined;
  const hasPerDateParam = perDate !== undefined;
  const shareholderTimestamp = validPerDate
    ? atTime(validPerDate, "23:59").toISOString()
    : undefined;
  const dispatch = useDispatch();
  const shareholdersStore = useShareholdersStore();
  const shareholderPerDateFilter = hasPerDateParam
    ? shareholdersStore.shareholderDateFilter
    : undefined;
  const shareholderId = Number(props.id);
  const alertStore = useMessageStore();
  const { f, t, lang } = useI18n();
  const userStore = useUserStore();
  const { apiService, http, optionsState } = useRootStore();
  const orgNo = userStore.orgNumber;
  const canEdit = userStore.userRole?.allowShareholdersEdit ?? true;
  const userRole = userStore.userRole;
  const allowOptionsView = userRole?.allowFinancialInstrumentsView ?? false;
  const allowCapTableView =
    Boolean(userRole?.allowCapTableView) &&
    Boolean(userStore.featureToggles.financialInstrumentOptions);
  const state = shareholdersStore.getShareholderDetailsState();
  const optionsData = optionsPageData(optionsState);
  const instrumentHolder =
    optionsData === undefined
      ? undefined
      : holderForShareholder(optionsData.holders, shareholderId);
  const optionsTabEnabled = allowOptionsView && instrumentHolder !== undefined;
  const referrer = navigationReferrerUrl(location.state);
  const arrivedFromOptions =
    referrer !== undefined && referrer.startsWith(adminShareholdersOptions(orgNo));
  const commitmentCtrl = useMemo(
    () =>
      new ShareholderCommitmentController(
        apiService,
        alertStore,
        orgNo,
        state.shareholder?.commitments,
      ),
    [alertStore, apiService, orgNo, state.shareholder],
  );
  const contributionCtrl = useMemo(
    () =>
      new ShareholderContributionController(
        apiService,
        alertStore,
        orgNo,
        state.shareholder?.shareholderContributions,
      ),
    [apiService, alertStore, orgNo, state.shareholder],
  );
  const commitmentsEnabled =
    props.allowCommitments && state.shareholder?.featureToggles.committedCapital;
  const contributionsEnabled = isContributionsEnabled(orgNo);
  const context: ShareholderDetailsContext = {
    orgNo,
    userRole,
    companyName: shareholdersStore.userStore.companyInfo?.company.name,
    shareholderId,
    activeTab: toShareholderDetailsTab(props.tab),
    commitmentsEnabled,
    commitments: commitmentCtrl.fetchShareholderCommitmentsResult,
    contributionsEnabled,
    contributions: contributionCtrl.fetchShareholderContributionsResult,
    optionsTabEnabled,
    optionHolderSince:
      optionsData !== undefined && instrumentHolder !== undefined
        ? optionHolderSince(instrumentHolder, optionsData.grants)
        : undefined,
    shareholderSelectorItems:
      arrivedFromOptions && optionsData !== undefined
        ? holderSelectorItems(optionsData.holders)
        : undefined,
    canEdit,
    perDateFilter: shareholderPerDateFilter,
    t,
    f,
    lang,
  };
  const companyName = context.companyName;
  const actions = prepShareholderDetailsActions({
    activeTab: context.activeTab,
    dispatch,
    lang,
    orgNo: context.orgNo,
    setShareholderStatusFilter: shareholdersStore.setShareholderStatusFilter,
    shareholderId,
    referrerUrl:
      navigationReferrerUrl(location.state) ?? adminShareholders(context.orgNo) + queryString,
    shareholderUrl: (orgNo, shareholderId, tab) =>
      adminShareholderDetails(orgNo, shareholderId, tab) + queryString,
    shareholderDetailsStore: shareholdersStore.shareholderDetailsStore,
    commitmentCtrl,
    currentUrl: location.pathname,
    canEdit,
    transactionDetailsUrl: (...args) => adminShareholdersTransaction(...args) + queryString,
    commitmentsUrl: adminShareholdersShareholderCommitments,
    commitmentDetailsUrl: adminShareholdersShareholderCommitmentDetails,
    contributionCtrl,
    contributionsUrl: adminShareholdersShareholderContributions,
    contributionDetailsUrl: adminShareholdersShareholderContributionDetails,
    editDetails: () =>
      shareholdersStore.openEditShareholderDialog({
        locale: lang,
        shareholderId,
        onSuccess: () => {
          shareholdersStore.getShareholderDetails(shareholderId, shareholderTimestamp);
          shareholdersStore.loadAllShareholders();
        },
      }),
    deleteShareholder: () =>
      shareholdersStore.deleteShareholder(shareholderId, () =>
        dispatch([
          {
            type: "navigate",
            url:
              navigationReferrerUrl(location.state) ??
              adminShareholders(context.orgNo) + queryString,
          },
        ]),
      ),
    deleteRepresentative: (id) => shareholdersStore.deleteRepresentative(id),
    downloadShareholderCertificate: () =>
      shareholdersStore.downloadShareholderCertificate(shareholderId, lang),
    apiService,
    messageStore: alertStore,
    http,
    user: userStore.user,
    companyName,
  });

  useEffect(() => {
    if (
      hasPerDateParam &&
      shareholdersStore.shareholderDateFilter.validatedValue !== validPerDate
    ) {
      shareholdersStore.shareholderDateFilter.setAndCommitInputValue(validPerDate);
    }
  }, [hasPerDateParam, shareholdersStore, validPerDate]);

  useEffect(() => {
    if (!hasPerDateParam) {
      return;
    }
    const currentPerDate = fromQueryString(location.search).perDate;
    const nextPerDate = shareholdersStore.shareholderDateFilter.validatedValue;
    if (currentPerDate === nextPerDate) {
      return;
    }
    const nextQueryString = toQueryString({
      ...fromQueryString(location.search),
      perDate: nextPerDate,
    });
    dispatch([
      {
        type: "navigate",
        url: `${location.pathname}${nextQueryString}`,
        replace: true,
        state: location.state,
      },
    ]);
  }, [
    dispatch,
    hasPerDateParam,
    location.pathname,
    location.search,
    location.state,
    shareholdersStore.shareholderDateFilter.validatedValue,
  ]);

  useEffect(() => {
    if (hasPerDateParam) {
      shareholdersStore.setShareholderStatusFilter("current");
    }
  }, [hasPerDateParam, shareholdersStore]);

  useEffect(() => {
    if (allowOptionsView) {
      dispatch(initOptionsPage(orgNo));
    }
    if (allowOptionsView && allowCapTableView) {
      dispatch(loadOptionsCapTable(orgNo));
    }
    return () => dispatch([clearOptionsState()]);
  }, [allowOptionsView, allowCapTableView, dispatch, orgNo]);

  useEffect(() => {
    shareholdersStore.clearMessages();
    shareholdersStore.getShareholderDetails(shareholderId, shareholderTimestamp);
    if (
      shareholdersStore.orgNo !== context.orgNo ||
      shareholdersStore.shareholders.every((s) => s.id !== shareholderId)
    ) {
      shareholdersStore.loadAllShareholders();
    }
    shareholdersStore.getTransactionLog();
    return () => {
      shareholdersStore.shareholderDetailsStore.resetShareholder();
    };
  }, [context.orgNo, shareholderId, shareholdersStore, shareholderTimestamp]);

  useEffect(() => {
    if (
      (props.tab === "commitments" && !commitmentsEnabled) ||
      (props.tab === "contributions" && !contributionsEnabled)
    ) {
      dispatch([
        {
          type: "navigate",
          url: adminShareholderDetails(orgNo, shareholderId, "overview") + queryString,
          replace: true,
        },
      ]);
    }
  }, [
    commitmentsEnabled,
    contributionsEnabled,
    dispatch,
    orgNo,
    props.tab,
    queryString,
    shareholderId,
  ]);

  if (allowOptionsView && isLoadingOptions(optionsState)) {
    return <LoadingIndicator fullscreen />;
  }

  return (
    <>
      <ShareholderDetailsPageView
        alertStore={alertStore}
        context={context}
        state={state}
        actions={actions}
        header={
          context.activeTab === "overview" &&
          shareholdersStore.shareholderDateFilter.validatedValue ? (
            <Container maxWidth={"xl"} marginY={"lg"}>
              <PerDateFilterAlert
                date={shareholdersStore.shareholderDateFilter.validatedValue}
                onReset={shareholdersStore.clearDateFilterAndLoadCurrent}
              />
            </Container>
          ) : undefined
        }
      />
      <ShareholderDetailsDialogs
        {...prepShareholderDetailsDialogs(
          shareholdersStore,
          commitmentCtrl,
          contributionCtrl,
          context,
          state,
        )}
      />
    </>
  );
});
